/**
 * Shielded ZEC balance + spendable-note polling.
 *
 * Two-tier sync model (same shape as leodex-v2's hook, distilled to
 * pure functions here so React layer can call it from anywhere):
 *
 *   - Cursor-based delta scans from `lastSyncedHeight + 1`, polling
 *     every ~90 s in the background.
 *   - Each scan returns NEW notes only; surviving notes from prior
 *     scans keep their witness paths in our persisted state. We replace
 *     ALL witnesses with a fresh scan from cold-start before send
 *     (the `forceFreshSync` path) because anchor + witness must agree.
 *
 * Persistence is in IndexedDB-friendly localStorage keys keyed by a
 * 16-hex-byte truncation of SHA-256(UFVK) so multiple wallets coexist.
 */

import { ShieldedHttpError, type ZecNoteWithProof } from "leokit-shielded-sdk";
import { getShieldedClient } from "./client";

export interface SpendableNote {
  nullifier: string;
  value_zats: number;
  position: number;
  recipient: string;
  rseed: string;
  witness: string[];
  height: number;
  txid: string;
  /**
   * Plaintext memo the SDK decrypted server-side using our UFVK.
   * UTF-8 string, up to 512 bytes (ZIP-302). Null for notes with the
   * empty memo (the default). Surfaced in the Wallet's incoming-history.
   */
  memo: string | null;
}

/**
 * A nullifier locally tagged as "spent by a tx we just broadcasted"
 * but not yet confirmed on-chain. We keep the note in `unspentNotes`
 * so the scanner can still ask the relay about it; we just exclude
 * it from `netBalanceZats` so the UI doesn't promise the user money
 * they're about to spend. If the scanner observes the nullifier in
 * the chain's spent set, this entry is dropped AND the underlying
 * note is removed (real spend). If `marked_at` is older than
 * PENDING_SPEND_TIMEOUT_MS without on-chain observation, we treat the
 * broadcast as failed and clear the pending mark — funds re-appear.
 */
export interface PendingSpentNullifier {
  nullifier: string;
  /** Wallet-side txid we issued. Lets the UI link the pending mark
   *  back to a row in tx-history. */
  txid: string;
  /** ms since epoch when we marked it. Used for the timeout sweep. */
  marked_at: number;
}

export interface DeltaState {
  lastSyncedHeight: number;
  knownNullifiers: string[];
  totalReceivedZats: number;
  netBalanceZats: number;
  unspentNotes: SpendableNote[];
  anchor: string | null;
  anchorHeight: number | null;
  /**
   * Reversible "I just broadcasted a spend of these notes" markers.
   * Empty in normal steady state. Populated by `markSpentLocally` and
   * pruned either by (a) the scanner observing the spend on-chain or
   * (b) PENDING_SPEND_TIMEOUT_MS elapsing without confirmation (treat
   * broadcast as failed → restore funds).
   */
  pendingSpentNullifiers: PendingSpentNullifier[];
}

/**
 * How long we trust an optimistic spent-mark before assuming the
 * broadcast silently failed (relay accepted but didn't propagate, tx
 * was malformed, mempool dropped, …). After this many milliseconds,
 * the pending mark is cleared and the note rejoins `netBalanceZats`.
 *
 * 30 minutes is comfortably longer than Zcash's expected confirmation
 * time (~75 s per block; even a slow mempool puts a healthy tx in a
 * block within minutes) but short enough that a user staring at a
 * stuck balance gets recovery without restarting the app.
 */
export const PENDING_SPEND_TIMEOUT_MS = 30 * 60 * 1000;

export interface ShieldedBalance {
  netBalanceZats: number;
  unspentNoteCount: number;
  anchorHeight: number | null;
  lastSyncedHeight: number;
}

export const EMPTY_STATE: DeltaState = {
  lastSyncedHeight: 0,
  knownNullifiers: [],
  totalReceivedZats: 0,
  netBalanceZats: 0,
  unspentNotes: [],
  anchor: null,
  anchorHeight: null,
  pendingSpentNullifiers: [],
};

export const EMPTY_BALANCE: ShieldedBalance = {
  netBalanceZats: 0,
  unspentNoteCount: 0,
  anchorHeight: null,
  lastSyncedHeight: 0,
};

// Sapling activation was block 419200 (mainnet). For ZcashWallet we floor
// the cold-start scan to a much later height — any earlier and we
// re-scan 5 years of empty blocks for a fresh wallet. Matches the
// COLD_START_FROM_HEIGHT in leodex-v2's hook.
const COLD_START_FROM_HEIGHT = 2_400_000;

const KEY_PREFIX = "telecrypt.shielded.delta-state.";

async function hashUfvkShort(ufvk: string): Promise<string> {
  const enc = new TextEncoder().encode(ufvk);
  const data = await crypto.subtle.digest("SHA-256", enc as BufferSource);
  const arr = new Uint8Array(data).slice(0, 16);
  return Array.from(arr, (b) => b.toString(16).padStart(2, "0")).join("");
}

async function storageKey(ufvk: string): Promise<string> {
  return KEY_PREFIX + (await hashUfvkShort(ufvk));
}

async function loadState(ufvk: string): Promise<DeltaState> {
  if (typeof localStorage === "undefined") return EMPTY_STATE;
  try {
    const raw = localStorage.getItem(await storageKey(ufvk));
    if (!raw) return EMPTY_STATE;
    const parsed = JSON.parse(raw) as Partial<DeltaState>;
    return {
      ...EMPTY_STATE,
      ...parsed,
      knownNullifiers: parsed.knownNullifiers ?? [],
      unspentNotes: parsed.unspentNotes ?? [],
      // Older states won't have this field — treat as empty.
      pendingSpentNullifiers: parsed.pendingSpentNullifiers ?? [],
    };
  } catch {
    return EMPTY_STATE;
  }
}

async function saveState(ufvk: string, state: DeltaState): Promise<void> {
  if (typeof localStorage === "undefined") return;
  try {
    localStorage.setItem(await storageKey(ufvk), JSON.stringify(state));
  } catch {
    // Quota / privacy mode — silent, the next scan will re-derive
  }
}

// Per-UFVK mutex chain. Every read-modify-write of DeltaState (the
// poller, forceFreshSync, markSpentLocally) routes through this so a
// 90s poll started before a send can't overwrite the send's spent-note
// pruning when it finally resolves. Each chain is a Promise queue
// keyed by UFVK hash; queued ops await the previous one.
const mutexByUfvk = new Map<string, Promise<unknown>>();

async function withStateLock<T>(ufvk: string, fn: () => Promise<T>): Promise<T> {
  const prev = mutexByUfvk.get(ufvk) ?? Promise.resolve();
  const next = prev.then(fn, fn);
  // Store the chain — note that we install `.then(fn, fn)` so even if a
  // prior op rejected, the next one still runs. Caller's rejections
  // propagate to their awaiter via the returned promise.
  mutexByUfvk.set(
    ufvk,
    next.catch(() => undefined),
  );
  return next;
}

interface DeltaOutcomeLike {
  to_height: number;
  notes?: ZecNoteWithProof[];
  received_zats?: number;
  net_received_zats?: number;
  spent?: Array<string | { nullifier: string }>;
  anchor?: string | null;
  anchor_height?: number | null;
}

// Exported for unit testing the pending-spent reconciliation logic;
// production callers go through pollShieldedBalance / forceFreshSync.
export function applyDelta(prev: DeltaState, outcome: DeltaOutcomeLike): DeltaState {
  const spentSet = new Set<string>(
    (outcome.spent ?? []).map((e) =>
      typeof e === "string" ? e : e.nullifier,
    ),
  );
  const surviving = prev.unspentNotes.filter(
    (n) => !spentSet.has(n.nullifier),
  );

  const newNotes: SpendableNote[] = (outcome.notes ?? [])
    .filter(
      (n: ZecNoteWithProof) =>
        !n.spent && Array.isArray(n.witness) && n.witness.length > 0,
    )
    .map((n: ZecNoteWithProof) => ({
      nullifier: n.nullifier,
      value_zats: n.amount_zats,
      position: n.position,
      recipient: n.recipient,
      rseed: n.rseed,
      witness: n.witness ?? [],
      height: outcome.to_height,
      txid: n.txid,
      memo: n.memo ?? null,
    }));

  const unspentNotes = [...surviving, ...newNotes];
  const knownNullifiers = unspentNotes.map((n) => n.nullifier);

  // Reconcile pending-spent markers with what the scanner just observed.
  // Two ways an entry can leave the list:
  //
  //   1) The scanner saw the nullifier as spent on-chain — real spend
  //      confirmed. Drop the pending mark; the note is already gone
  //      from `unspentNotes` because the spentSet filter above removed
  //      it (line: surviving = filter).
  //   2) PENDING_SPEND_TIMEOUT_MS elapsed without confirmation — treat
  //      the broadcast as failed. Drop the pending mark; the note is
  //      still in `unspentNotes` so its value re-joins the balance.
  //
  // Note kept entries (still pending, not timed-out) continue to be
  // excluded from `netBalanceZats` below.
  const now = Date.now();
  const stillPending = prev.pendingSpentNullifiers.filter((p) => {
    const observedOnChain = spentSet.has(p.nullifier);
    const timedOut = now - p.marked_at > PENDING_SPEND_TIMEOUT_MS;
    return !observedOnChain && !timedOut;
  });
  const stillPendingSet = new Set(stillPending.map((p) => p.nullifier));

  // netBalanceZats is derived, not accumulated. Earlier versions added
  // `outcome.net_received_zats` to `prev.netBalanceZats` on every poll,
  // which permanently corrupted the balance on any duplicate scan, HTTP
  // retry, or poll/send race. The authoritative source of truth is the
  // unspent-note set — sum its values and you can't drift.
  //
  // Pending-spent nullifiers are subtracted from the displayed balance
  // (you don't want to promise the user money they're about to spend)
  // but their notes stay in `unspentNotes` so the scanner can ask the
  // relay whether the spend actually landed on-chain.
  const netBalanceZats = unspentNotes.reduce(
    (sum, n) => sum + (stillPendingSet.has(n.nullifier) ? 0 : n.value_zats),
    0,
  );

  return {
    lastSyncedHeight: outcome.to_height,
    knownNullifiers,
    totalReceivedZats: prev.totalReceivedZats + (outcome.received_zats ?? 0),
    netBalanceZats,
    unspentNotes,
    anchor: outcome.anchor ?? prev.anchor,
    anchorHeight: outcome.anchor_height ?? prev.anchorHeight,
    pendingSpentNullifiers: stillPending,
  };
}

/**
 * Incremental sync — runs in the background polling loop. Cheap: only
 * scans new blocks since the cursor.
 *
 * The optional `onNewNotes` hook fires once per poll with every note
 * discovered in this delta. We use this to feed an "inbound history"
 * cache without mixing storage concerns into the scanner itself.
 */
export async function pollShieldedBalance(
  ufvk: string,
  onNewNotes?: (notes: SpendableNote[]) => void,
): Promise<ShieldedBalance> {
  return withStateLock(ufvk, () => pollShieldedBalanceImpl(ufvk, onNewNotes));
}

async function pollShieldedBalanceImpl(
  ufvk: string,
  onNewNotes?: (notes: SpendableNote[]) => void,
): Promise<ShieldedBalance> {
  const client = getShieldedClient();
  const before = await loadState(ufvk);
  const fromHeight =
    before.lastSyncedHeight > 0
      ? before.lastSyncedHeight + 1
      : COLD_START_FROM_HEIGHT;

  try {
    const outcome = await client.scannerDelta({
      ufvk,
      fromHeight,
      knownNullifiers: before.knownNullifiers,
      withWitnesses: true,
    });
    const next = applyDelta(before, outcome);
    await saveState(ufvk, next);
    if (onNewNotes) {
      // The notes the scanner returned that weren't already in our
      // unspent set before this poll. Equivalent to applyDelta's
      // "newNotes" computation but exposed for history bookkeeping.
      const beforeNullifiers = new Set(before.knownNullifiers);
      const newOnes = next.unspentNotes.filter(
        (n) => !beforeNullifiers.has(n.nullifier),
      );
      if (newOnes.length > 0) onNewNotes(newOnes);
    }
    return {
      netBalanceZats: next.netBalanceZats,
      unspentNoteCount: next.unspentNotes.length,
      anchorHeight: next.anchorHeight,
      lastSyncedHeight: next.lastSyncedHeight,
    };
  } catch (e) {
    // 503 = backend backfill still in progress. Surface a state that
    // tells the UI "scanner not ready" without losing what we already
    // had.
    if (e instanceof ShieldedHttpError && e.status === 503) {
      return {
        netBalanceZats: before.netBalanceZats,
        unspentNoteCount: before.unspentNotes.length,
        anchorHeight: before.anchorHeight,
        lastSyncedHeight: before.lastSyncedHeight,
      };
    }
    throw e;
  }
}

/**
 * One-shot read of every note currently in the persisted unspent set.
 * Used by the wallet to backfill its incoming-history log from a
 * scanner state that was populated before history tracking existed.
 */
export async function readUnspentNotes(ufvk: string): Promise<SpendableNote[]> {
  const state = await loadState(ufvk);
  return state.unspentNotes;
}

/**
 * Full rescan from cold-start. Discards locally-cached note witnesses
 * (which may have aged out of consistency with the latest anchor) and
 * rebuilds DeltaState from a single fresh /scanner/delta response.
 *
 * Call this immediately before signing — guarantees anchor + every
 * spend witness agree on the same Merkle root. The leodex-v2 send flow
 * proved this was necessary after the SaplingBuild(AnchorMismatch)
 * bug; same fix applies here.
 */
export async function forceFreshSync(ufvk: string): Promise<{
  notes: SpendableNote[];
  anchor: string | null;
  anchorHeight: number | null;
  netBalanceZats: number;
  lastSyncedHeight: number;
}> {
  return withStateLock(ufvk, async () => {
    const client = getShieldedClient();
    const outcome = await client.scannerDelta({
      ufvk,
      fromHeight: COLD_START_FROM_HEIGHT,
      knownNullifiers: [],
      withWitnesses: true,
    });
    const fresh = applyDelta(EMPTY_STATE, outcome);
    await saveState(ufvk, fresh);
    return {
      notes: fresh.unspentNotes,
      anchor: fresh.anchor,
      anchorHeight: fresh.anchorHeight,
      netBalanceZats: fresh.netBalanceZats,
      lastSyncedHeight: fresh.lastSyncedHeight,
    };
  });
}

/**
 * Read-only spendables snapshot — what the send flow consumes after a
 * forceFreshSync. Does NOT touch the network.
 */
export async function getSpendables(ufvk: string): Promise<{
  notes: SpendableNote[];
  anchor: string | null;
  anchorHeight: number | null;
  lastSyncedHeight: number;
  netBalanceZats: number;
}> {
  const s = await loadState(ufvk);
  return {
    notes: s.unspentNotes,
    anchor: s.anchor,
    anchorHeight: s.anchorHeight,
    lastSyncedHeight: s.lastSyncedHeight,
    netBalanceZats: s.netBalanceZats,
  };
}

/**
 * Optimistically mark spent nullifiers locally after a successful
 * broadcast — but **reversibly**. Earlier versions of this function
 * deleted the notes outright, which means a relay that accepts a tx
 * but fails to propagate it (silently dropped, malformed for the
 * network, doomed by a stale anchor, …) would permanently zero the
 * user's displayed balance even though the funds were still on-chain.
 *
 * The fix:
 *   - Notes stay in `unspentNotes` AND their nullifiers stay in
 *     `knownNullifiers`, so the scanner can still ask the relay
 *     "are these spent?" on every poll.
 *   - The pending mark is added to `pendingSpentNullifiers` with a
 *     wallet txid + timestamp.
 *   - `applyDelta` excludes pending nullifiers from `netBalanceZats`
 *     so the displayed balance still reflects what the user is about
 *     to spend.
 *   - On each delta the scanner observes: pending entries either
 *     (a) match a real on-chain spend (`spent` set) → the underlying
 *     note is removed for good, OR (b) hit PENDING_SPEND_TIMEOUT_MS
 *     without a chain observation → the pending mark is dropped and
 *     the note's value rejoins the balance.
 */
export async function markSpentLocally(
  ufvk: string,
  spentNullifiers: string[],
  txid: string,
): Promise<void> {
  if (spentNullifiers.length === 0) return;
  await withStateLock(ufvk, async () => {
    const state = await loadState(ufvk);
    const knownSet = new Set(state.unspentNotes.map((n) => n.nullifier));
    const now = Date.now();
    // Dedup against the existing pending list — a poll/send race
    // could end up calling this twice for the same nullifier.
    const existingPending = new Set(
      state.pendingSpentNullifiers.map((p) => p.nullifier),
    );
    const fresh: PendingSpentNullifier[] = spentNullifiers
      .filter((n) => knownSet.has(n) && !existingPending.has(n))
      .map((nullifier) => ({ nullifier, txid, marked_at: now }));
    if (fresh.length === 0) return;
    const pendingSpentNullifiers = [...state.pendingSpentNullifiers, ...fresh];
    const pendingSet = new Set(
      pendingSpentNullifiers.map((p) => p.nullifier),
    );
    await saveState(ufvk, {
      ...state,
      pendingSpentNullifiers,
      // Recompute the displayed balance from the unspent set minus
      // anything currently in the pending-spent list.
      netBalanceZats: state.unspentNotes.reduce(
        (s, n) => s + (pendingSet.has(n.nullifier) ? 0 : n.value_zats),
        0,
      ),
    });
  });
}

/**
 * Manual recovery escape hatch: clear ALL pending-spent markers for
 * this UFVK. Restores any funds that were optimistically deducted but
 * whose broadcasts never landed on-chain. Wired to a "Recover balance"
 * button in the Wallet so users stuck on a silently-dropped tx don't
 * need to wipe their local data and re-import the seed.
 *
 * Safe to call even when no marks are stale; no-ops in that case. Does
 * NOT trigger a scanner poll — the caller should refresh from balance
 * immediately after, or wait for the next 90s tick.
 */
export async function clearAllPendingSpends(ufvk: string): Promise<{
  cleared: number;
  netBalanceZats: number;
}> {
  return withStateLock(ufvk, async () => {
    const state = await loadState(ufvk);
    if (state.pendingSpentNullifiers.length === 0) {
      return { cleared: 0, netBalanceZats: state.netBalanceZats };
    }
    const cleared = state.pendingSpentNullifiers.length;
    const netBalanceZats = state.unspentNotes.reduce(
      (s, n) => s + n.value_zats,
      0,
    );
    await saveState(ufvk, {
      ...state,
      pendingSpentNullifiers: [],
      netBalanceZats,
    });
    return { cleared, netBalanceZats };
  });
}
